import { authenticatedFetch } from "@lumi/data";
import { useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

interface ProviderStats {
  total: number;
  draft: number;
  pending: number;
  approved: number;
  rejected: number;
  published: number;
}

interface RecentSubmission {
  id: string;
  title: string;
  status: string;
  synopsis: string | null;
  posterUrl: string | null;
  year: number | null;
  durationMinutes: number | null;
  director: string | null;
  submittedAt?: string | null;
  createdAt: string;
}

export interface Provider {
  id: string;
  email: string;
  organization: string | null;
  createdAt: string;
  stats: ProviderStats;
  recentSubmission: RecentSubmission | null;
}

interface ProvidersResponse {
  data: Provider[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ProviderDetailResponse {
  provider: {
    id: string;
    email: string;
    organization: string | null;
    metadata: unknown;
    createdAt: string;
  };
  stats: ProviderStats;
  recentSubmissions: RecentSubmission[];
}

/**
 * Hook to fetch list of content providers with statistics
 */
export function useProviders(searchQuery = "", page = 1, limit = 20) {
  const { accessToken, refreshAuth } = useAuth();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    const fetchProviders = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append("search", searchQuery);
        params.append("page", page.toString());
        params.append("limit", limit.toString());

        const url = `${API_BASE}/v1/admin/providers?${params.toString()}`;

        const response = await authenticatedFetch<ProvidersResponse>(
          url,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setProviders(response.data);
        setPagination(response.pagination);
      } catch (err) {
        console.error("Failed to fetch providers:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch providers");
      } finally {
        setLoading(false);
      }
    };

    void fetchProviders();
  }, [accessToken, refreshAuth, searchQuery, page, limit, API_BASE]);

  return { providers, pagination, loading, error };
}

/**
 * Hook to fetch detailed provider information
 */
export function useProviderDetails(providerId: string | undefined) {
  const { accessToken, refreshAuth } = useAuth();
  const [provider, setProvider] = useState<ProviderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  useEffect(() => {
    if (!accessToken || !providerId) {
      setLoading(false);
      return;
    }

    const fetchProviderDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const url = `${API_BASE}/v1/admin/providers/${providerId}`;

        const response = await authenticatedFetch<ProviderDetailResponse>(
          url,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setProvider(response);
      } catch (err) {
        console.error("Failed to fetch provider details:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch provider details");
      } finally {
        setLoading(false);
      }
    };

    void fetchProviderDetails();
  }, [accessToken, refreshAuth, providerId, API_BASE]);

  return { provider, loading, error };
}
